import { memo } from "react";
import type { Membership } from "../../../lib/queries/memberships";
import { MemberPhoto } from "../../../components/MemberPhoto";
import styles from "./TransferRevize.module.css";

/**
 * One transfer candidate as a toggleable photo chip. Selected is the marked
 * state — check badge on the photo, primary-tinted surface — because the
 * revize opens with everyone selected and the leader's work is unselecting
 * the few who left: the exceptions must be the chips that look different.
 *
 * A native <button>, not the lib Button: a pill wrapping a photo with a
 * corner badge is nothing Button can be talked into (no tinted/pressed
 * look, no avatar slot) — documented in AGENTS §3 with the other
 * exceptions; the upstream proposal is docs/adminer-redesign.md §27.
 */
export const PersonChip = memo(function PersonChip({
  membership: m,
  selected,
  dimmed = false,
  onToggle,
}: {
  membership: Membership;
  selected: boolean;
  /** True while a search query is active and this chip is not a match.
   * Dimmed, never hidden: a chip that disappears makes the commit look
   * smaller than it is. */
  dimmed?: boolean;
  onToggle: (m: Membership) => void;
}) {
  const name = `${m.person.first_name} ${m.person.last_name}`;
  const cls = [styles.chip, selected && styles.chipOn, dimmed && styles.chipDim]
    .filter(Boolean)
    .join(" ");
  return (
    // The two gaps this used to stand on are closed in 0.31.0: `pressed`
    // gives the tinted toggle face, and `startIcon` takes the photo. What is
    // untested is the geometry — this pill is sized around a 26px MemberPhoto
    // with a corner badge overlapping it, where Button pads for text. It may
    // well convert by scoping `--adminer-control-height`; nobody has looked
    // at it rendered, and guessing wrong here is a visible regression on the
    // revize panel. Tracked with the rest of the surviving exceptions.
    // eslint-disable-next-line @marekl/adminer/prefer-lib-form-controls -- an avatar-sized pill (26px photo + corner badge) against Button's text-sized padding; the tinted-face and avatar-slot halves of this exception are gone as of 0.31.0
    <button
      type="button"
      className={cls}
      aria-pressed={selected}
      title={selected ? `Odznačit: ${name}` : `Označit k převodu: ${name}`}
      onClick={() => onToggle(m)}
    >
      {/* Decorative here: the chip itself names the person, so the photo's
          own labels („Fotka: …" / „Chybí foto: …") must not leak into the
          button's accessible name. */}
      <span className={styles.chipPhoto} aria-hidden>
        <MemberPhoto
          personId={m.person.id}
          firstName={m.person.first_name}
          lastName={m.person.last_name}
          hasPhoto={m.person.photo_uploaded}
          size={26}
        />
        {selected && <span className={styles.chipCheck} aria-hidden />}
      </span>
      <span className={styles.chipName}>{name}</span>
      {m.kind === "guest" && (
        <span className={styles.chipNote}>
          {m.season_code ? `· v sezóně ${m.season_code} nečlen` : "· nečlen"}
        </span>
      )}
    </button>
  );
});
